import React from "react";
import { toast } from "sonner";
import {
  ClipboardList, Calendar, Download, Printer, Package, Weight, Boxes,
  AlertTriangle, CheckCircle2, MapPin, Loader2, Truck, Clock, RefreshCw,
} from "lucide-react";
import { businessApi } from "../../lib/api";

const todayDk = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function BusinessManifestsPage() {
  const [date, setDate] = React.useState(todayDk());
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(null); // { pickup_location_id, manifest data }
  const [detailLoading, setDetailLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get("/business/pickup-manifests", { params: { date } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente manifests");
    } finally {
      setLoading(false);
    }
  }, [date]);

  React.useEffect(() => { load(); }, [load]);

  const openDetail = async (loc_id) => {
    setDetailLoading(true);
    setSelected({ pickup_location_id: loc_id, data: null });
    try {
      const { data } = await businessApi().get(`/business/pickup-manifests/${loc_id}/${date}`);
      setSelected({ pickup_location_id: loc_id, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente manifest-detalje");
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (loc_id, name) => {
    try {
      const res = await businessApi().get(`/business/pickup-manifests/${loc_id}/${date}/manifest.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `manifest_${date}_${(name || "pickup").replace(/\s+/g, "_")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-download fejlede");
    }
  };

  const openPdfInline = (loc_id) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("ema_business_token");
    // Build a URL with token in query for printer; safer is to fetch+open blob URL
    businessApi().get(`/business/pickup-manifests/${loc_id}/${date}/manifest.pdf`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const w = window.open(url, "_blank");
        if (w) {
          setTimeout(() => { try { w.print(); } catch { /* print failed silently */ } }, 1500);
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Kunne ikke åbne manifest"));
  };

  return (
    <div className="space-y-6" data-testid="business-manifests-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-brand-red font-bold">SPRINT 2E</div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Pickup-manifests</h1>
        <p className="text-brand-text2 mt-2">
          Få oversigt over dine forsendelser klar til afhentning, opdelt per pickup-lokation. Print eller download som PDF for chaufføren.
        </p>
      </header>

      {/* Date filter */}
      <div className="flex flex-wrap gap-3 items-center bg-white border border-brand p-4">
        <label className="text-sm font-semibold text-brand-dark inline-flex items-center gap-2">
          <Calendar size={14}/> Dato:
        </label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          data-testid="biz-manifest-date"
          className="input-flat py-1.5 max-w-[180px]"/>
        <button onClick={load} className="text-sm text-brand-text2 hover:text-brand-dark inline-flex items-center gap-1" data-testid="biz-manifest-reload">
          <RefreshCw size={14}/> Opdater
        </button>
        <span className="text-xs text-brand-text2 ml-auto">{items.length} pickup-lokation(er) med forsendelser</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-brand-text2 inline-flex items-center justify-center gap-2"><Loader2 className="animate-spin"/> Indlæser...</div>
      ) : items.length === 0 ? (
        <div className="border border-brand bg-white p-12 text-center">
          <ClipboardList size={48} className="mx-auto text-brand-text2 mb-3"/>
          <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Ingen forsendelser at manifestere</h3>
          <p className="text-sm text-brand-text2">Der er ingen åbne forsendelser oprettet på den valgte dato. Opret nogle under «Opret forsendelse» eller via CSV-import.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((m) => (
            <ManifestCard key={m.pickup_location_id} m={m} onOpen={() => openDetail(m.pickup_location_id)}
              onDownload={() => downloadPdf(m.pickup_location_id, m.pickup_location_name)}
              onPrint={() => openPdfInline(m.pickup_location_id)}/>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <DetailModal
          loading={detailLoading}
          data={selected.data}
          onClose={() => setSelected(null)}
          onDownload={() => downloadPdf(selected.pickup_location_id, selected.data?.pickup_location?.name)}
          onPrint={() => openPdfInline(selected.pickup_location_id)}
        />
      )}
    </div>
  );
}

function ManifestCard({ m, onOpen, onDownload, onPrint }) {
  const a = m.aggregate || {};
  return (
    <div className="border border-brand bg-white p-5" data-testid={`biz-manifest-card-${m.pickup_location_id}`}>
      <div className="flex justify-between items-start gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-red font-bold">{m.manifest_number}</div>
          <h3 className="font-display font-bold text-lg text-brand-dark mt-1 flex items-center gap-2">
            <MapPin size={16} className="text-brand-red"/>
            {m.pickup_location_name}
          </h3>
          <div className="text-sm text-brand-text2">{m.pickup_location_address}, {m.pickup_location_postal_code} {m.pickup_location_city}</div>
          {(m.pickup_window_from || m.pickup_window_to) && (
            <div className="text-xs text-brand-text2 mt-1 inline-flex items-center gap-1">
              <Clock size={12}/> Pickup-vindue: <b>{m.pickup_window_from || "—"}–{m.pickup_window_to || "—"}</b>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        <Stat label="Forsendelser" value={a.total_shipments} icon={Package}/>
        <Stat label="Kolli" value={a.total_colli} icon={Boxes}/>
        <Stat label="Vægt" value={`${(a.total_weight_kg || 0).toFixed(1)} kg`} icon={Weight}/>
        <Stat label="Volume" value={`${(a.total_volume_l || 0).toFixed(0)} L`} icon={Truck}/>
      </div>
      <div className="flex flex-wrap gap-4 text-xs mb-4">
        <span className="inline-flex items-center gap-1 text-brand-red font-semibold">SD {a.same_day_count || 0}</span>
        <span className="inline-flex items-center gap-1 text-brand-dark font-semibold">ND {a.next_day_count || 0}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onOpen} className="text-xs font-semibold text-brand-dark hover:text-brand-red inline-flex items-center gap-1" data-testid={`biz-manifest-view-${m.pickup_location_id}`}>
          Vis detaljer →
        </button>
        <div className="ml-auto flex gap-2">
          <button onClick={onPrint} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid={`biz-manifest-print-${m.pickup_location_id}`}>
            <Printer size={12}/> Print
          </button>
          <button onClick={onDownload} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-brand-red hover:opacity-90" data-testid={`biz-manifest-download-${m.pickup_location_id}`}>
            <Download size={12}/> PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-brand-bg2 px-2 py-2 text-center">
      <Icon size={12} className="mx-auto text-brand-text2 mb-1"/>
      <div className="text-base font-bold text-brand-dark leading-none">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-brand-text2 mt-0.5">{label}</div>
    </div>
  );
}

function DetailModal({ data, loading, onClose, onDownload, onPrint }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="biz-manifest-detail-modal">
      <div className="bg-white border border-brand max-w-3xl w-full mt-12 mb-12 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-brand px-5 py-4 flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-red font-bold">{data?.manifest_number || "Manifest"}</div>
            <h3 className="font-display font-bold text-xl text-brand-dark">{data?.pickup_location?.name || "Indlæser..."}</h3>
          </div>
          <button onClick={onClose} className="text-2xl text-brand-text2 hover:text-brand-dark" data-testid="biz-manifest-close">×</button>
        </div>

        {loading || !data ? (
          <div className="p-12 text-center text-brand-text2"><Loader2 className="animate-spin inline mr-2"/> Indlæser detaljer...</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Warnings */}
            {(data.missing_data_warnings || []).length > 0 && (
              <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm">
                <div className="font-bold text-amber-900 inline-flex items-center gap-1 mb-1"><AlertTriangle size={14}/> Manglende data</div>
                <ul className="list-disc list-inside text-amber-900 text-xs">
                  {data.missing_data_warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Vehicle check */}
            {data.vehicle_check && (
              <div className={`border-l-4 p-3 text-sm ${data.vehicle_check.blocking ? "border-red-500 bg-red-50" : "border-green-500 bg-green-50"}`}>
                <div className={`font-bold inline-flex items-center gap-1 mb-1 ${data.vehicle_check.blocking ? "text-red-900" : "text-green-900"}`}>
                  {data.vehicle_check.blocking ? <AlertTriangle size={14}/> : <CheckCircle2 size={14}/>}
                  Køretøjs-tjek
                </div>
                <div className="text-xs">
                  {data.vehicle_check.blocking ? data.vehicle_check.warning : `Volume fit ${data.vehicle_check.fit_volume_pct?.toFixed(0)}% · Vægt fit ${data.vehicle_check.fit_weight_pct?.toFixed(0)}%`}
                </div>
              </div>
            )}

            {/* Aggregate */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Forsendelser" value={data.aggregate?.total_shipments} icon={Package}/>
              <Stat label="Kolli" value={data.aggregate?.total_colli} icon={Boxes}/>
              <Stat label="Vægt" value={`${(data.aggregate?.total_weight_kg || 0).toFixed(1)} kg`} icon={Weight}/>
              <Stat label="Volume" value={`${(data.aggregate?.total_volume_l || 0).toFixed(0)} L`} icon={Truck}/>
            </div>

            {/* Route info */}
            <div className="bg-brand-bg2 p-3 text-sm">
              <div className="font-semibold text-brand-dark mb-1">Rute & loading</div>
              <div className="text-xs text-brand-text2">
                Zoner: {(data.route_info?.zones || []).join(", ") || "—"} · Estimeret loading: <b>{data.route_info?.estimated_loading_minutes || 0} min</b>
              </div>
            </div>

            {/* Shipments table */}
            <div>
              <h4 className="font-display font-bold text-base text-brand-dark mb-2">Forsendelser ({(data.shipments || []).length})</h4>
              <div className="border border-brand overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-brand-bg2 text-brand-text2 uppercase">
                    <tr>
                      <th className="px-2 py-2 text-left">Shipment</th>
                      <th className="px-2 py-2 text-left">Kunde-ref</th>
                      <th className="px-2 py-2 text-left">Modtager</th>
                      <th className="px-2 py-2 text-left">Postnr</th>
                      <th className="px-2 py-2 text-left">Service</th>
                      <th className="px-2 py-2 text-right">Vægt</th>
                      <th className="px-2 py-2 text-right">Kolli</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.shipments || []).map((s) => (
                      <tr key={s.id} className="border-t border-brand">
                        <td className="px-2 py-1.5 font-mono">{s.shipment_number}</td>
                        <td className="px-2 py-1.5">{s.customer_reference || "—"}</td>
                        <td className="px-2 py-1.5">{s.recipient_name}</td>
                        <td className="px-2 py-1.5">{s.delivery_postal_code} {s.delivery_city}</td>
                        <td className="px-2 py-1.5">{s.product === "same_day_b2b" ? "SD" : "ND"}</td>
                        <td className="px-2 py-1.5 text-right">{(s.weight_kg || 0).toFixed(1)} kg</td>
                        <td className="px-2 py-1.5 text-right">{s.colli_count || 1}</td>
                      </tr>
                    ))}
                    {(data.shipments || []).length === 0 && (
                      <tr><td colSpan={7} className="text-center text-brand-text2 py-4">Ingen forsendelser</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-3 border-t border-brand">
              <button onClick={onPrint} className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid="biz-manifest-detail-print">
                <Printer size={14}/> Print
              </button>
              <button onClick={onDownload} className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white bg-brand-red hover:opacity-90" data-testid="biz-manifest-detail-download">
                <Download size={14}/> Download PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
